import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from './ui/Button';
import TextInput from './ui/TextInput';
import { Label } from './ui/Label';
import { cn } from '../lib/utils';
import { APP_BRAND } from '../config/branding';
import AppBrandIcon from './branding/AppBrandIcon';
import { authService } from '../services/authService';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const { t } = useTranslation('login');
  const productName = appConfig.system.appName?.trim() || APP_BRAND.name;

  useDocumentTitle(t('resetTitle'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');
    setConfirmError('');

    if (!token) {
      setError(t('invalidResetLink'));
      return;
    }
    if (!password || password.length < 6) {
      setPasswordError(t('passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError(t('passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword(token, password);
      navigate('/login?reset=1');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || t('resetError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('min-h-screen flex items-center justify-center px-4 py-10', colors.background)}>
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <AppBrandIcon className="h-11 w-11" iconClassName="h-5 w-5" />
          <div>
            <p className={cn('text-lg font-bold', colors.text)}>{productName}</p>
            <p className={cn('text-sm', colors.textMuted)}>{t('resetHeading')}</p>
          </div>
        </div>

        <div className={cn('rounded-2xl border p-8 shadow-sm', colors.surface, colors.border)}>
          <h2 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>{t('resetTitle')}</h2>
          <p className={cn('mt-1 text-sm', colors.textMuted)}>{t('resetSubtitle')}</p>

          <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.error)} role="alert">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="password">{t('newPassword')}</Label>
              <TextInput
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                className="mt-1.5"
                aria-invalid={!!passwordError}
              />
              {passwordError && <p className="mt-1 text-sm text-red-500">{passwordError}</p>}
            </div>
            <div>
              <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
              <TextInput
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (confirmError) setConfirmError('');
                }}
                className="mt-1.5"
                aria-invalid={!!confirmError}
              />
              {confirmError && <p className="mt-1 text-sm text-red-500">{confirmError}</p>}
            </div>
            <Button type="submit" className="w-full" loading={isLoading} disabled={isLoading || !token}>
              {t('resetButton')}
            </Button>
            <Link to="/forgot-password" className="block text-center text-sm text-indigo-600 hover:text-indigo-700">
              {t('requestNewLink')}
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
